import SmartphoneIcon from '@mui/icons-material/Smartphone';
import {
  Box,
  Button,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRequestOTP } from './hooks/useRequestOTP';
import { SecuritySkeleton } from '../common/SecuritySkeleton';
import { LanguageSetting } from '../common/LanguageSetting';
import { LoadingIndicator, MessageBar } from '@xolv-mybrightlink/common';
import { useLogin } from './reducer/useLogin';

export const PhoneNumberInputForm = () => {
  const intl = useIntl();
  const loginState = useLogin();
  const stateData = loginState.data;
  const requestingOtpFailed =
    stateData.type === 'REQUESTING_OTP' && stateData.error === 'generic-error';

  return (
    <SecuritySkeleton>
      <PhoneNumberInput />
      <Typography
        variant='body2'
        sx={{
          color: '#626a7a',
          mb: 10,
        }}
      >
        <FormattedMessage id='login.otp.footer.signin-or-register.label1' />
        <br />
        <br />
        <FormattedMessage
          id='login.otp.footer.signin-or-register.label2'
          values={{
            link: (text) => (
              <Link
                href='https://www.mybrightlink.org'
                target='_blank'
                rel='noopener'
                sx={{
                  fontWeight: 500,
                }}
              >
                {text}
              </Link>
            ),
          }}
        />
      </Typography>
      <LanguageSetting />
      <MessageBar
        open={requestingOtpFailed}
        severity='error'
        message={intl.formatMessage({ id: 'common.error.message' })}
      />
      {loginState.loading && <LoadingIndicator fullHeight overlay />}
    </SecuritySkeleton>
  );
};

const PhoneNumberInput = () => {
  const intl = useIntl();
  const loginState = useLogin();
  const stateData = loginState.data;

  const [phoneNumber, setPhoneNumber] = useState<string>(stateData.phoneNumber);
  const requestOtp = useRequestOTP();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedInput = event.target.value.replace(/\D/g, '');
    if (sanitizedInput.length < 10) {
      setPhoneNumber(sanitizedInput);
    } else if (sanitizedInput.length === 10) {
      const number = sanitizedInput.replace(
        /(\d{3})(\d{3})(\d{4})/,
        '($1) $2-$3',
      );
      setPhoneNumber(number);
    }
  };

  const fireRequestOtp = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (phoneNumber) {
        await requestOtp(phoneNumber);
      }
    },
    [phoneNumber, requestOtp],
  );

  return (
    <Box
      sx={{
        mt: 24,
      }}
      component='form'
      noValidate
      onSubmit={fireRequestOtp}
    >
      <Typography
        variant='body2'
        sx={{
          color: '#626a7a',
        }}
      >
        {' '}
        <FormattedMessage id='login.otp.input.mobile.label' />
      </Typography>
      <TextField
        margin='normal'
        required
        fullWidth
        id='phone'
        role={'phoneInput'}
        name='phone'
        placeholder={intl.formatMessage({
          id: 'login.otp.input.mobile.placeholder',
        })}
        inputProps={{
          'data-testid': 'phoneInput',
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <SmartphoneIcon fontSize='small' />
            </InputAdornment>
          ),
        }}
        variant='outlined'
        sx={{
          '& fieldset': {
            borderRadius: 3,
            borderColor: 'action.active',
          },
          '& input::placeholder': {
            fontSize: 14,
            opacity: 0.8,
            color: 'action.active',
          },
        }}
        value={phoneNumber}
        onChange={handleChange}
        autoComplete='phone'
        autoFocus
      />
      <Button
        type='submit'
        fullWidth
        variant='contained'
        role={'requestOtp'}
        size='large'
        sx={{
          mt: 6,
          mb: 8,
          borderRadius: 30,
          py: 3.5,
        }}
        data-testid='requestOtpButton'
        disabled={!phoneNumber || phoneNumber.length == 0}
        disableElevation
      >
        <FormattedMessage id='login.otp.button.signin-or-register' />
      </Button>
    </Box>
  );
};
